import { useEffect, useRef, useState } from "react";

import { useFeatureFlagState } from "@vericus/cadmus-common";
import { CountBadge, Text, mainContentProps } from "@vericus/cadmus-ui";

import { ApolloError, NetworkStatus, useApolloClient } from "@apollo/client";
import {
  NavLink,
  Outlet,
  useMatch,
  useOutletContext,
  useParams,
} from "react-router-dom";

import { RootHeading } from "@/components/page-heading/root-heading";
import {
  AssessmentSettingsFragment,
  useStudentActionFeedQuery,
  WorkGroupDocument,
} from "@/generated/graphql";

import { actionsBadgeLabel } from "./actions-badge-label";
import { StudentActionFeedPayload } from "./action-feed";
import * as styles from "./home-page.css";

interface Props {
  assessmentSettings: AssessmentSettingsFragment | null;
}

/**
 * Feed data the index route's `StudentHome` reads back via
 * `useHomeOutletContext`. One ObservableQuery (below) feeds both the tab
 * badge and the feed, so the feed sees a real Apollo `networkStatus`.
 */
export interface HomeOutletContext {
  studentActionFeed: StudentActionFeedPayload | null | undefined;
  networkStatus: NetworkStatus;
  error?: ApolloError;
  loading: boolean;
  /**
   * Whether the one-off work-group refresh below is still in flight. No hook
   * exposes it, and `StudentHome`'s own `useWorkGroupQuery` is cache-first, so
   * on a warm re-entry that hook reports `loading: false` while this read is
   * still deciding whether the cached charter is the real one.
   */
  workGroupRefreshing: boolean;
}

export function useHomeOutletContext() {
  return useOutletContext<HomeOutletContext>();
}

/**
 * Student Home page with navigation tabs and status cards to navigate to
 * different parts of the Assessment. Currently only used in Group Assessments.
 */
export function HomePage(props: Props) {
  const { assessmentSettings } = props;
  const { assessmentId = "" } = useParams();
  const { enabled: studentHomeEnabled } = useFeatureFlagState("student_home");
  const client = useApolloClient();

  // Element on which the Cadmus Manual sidebar Modal will mount
  const manualMountRef = useRef(null);

  // The single feed query. `skip` keeps the flag's "default off" guarantee
  // that a student without it makes no request at all.
  const { data, error, loading, networkStatus, refetch } =
    useStudentActionFeedQuery({
      variables: { assessmentId },
      skip: !studentHomeEnabled,
      // cache-and-network so returning to /home paints the last feed from cache
      // immediately and refreshes in the background, instead of gating the page on
      // a network round-trip. StudentHome renders the feed whenever cached data is
      // present, so the cached list shows through while the refresh is in flight.
      fetchPolicy: "cache-and-network",
    });
  const studentActionFeed = data?.studentActionFeed;
  const badgeCount = studentActionFeed?.badgeCount ?? 0;

  // Refetch the feed and the work group together on entry to the index route,
  // never on other tab switches. `WorkGroupDocument` holds `charter.id`; the
  // feed refetch alone would leave it stale, so a teammate's just-created
  // charter would still route to a dead id. `useMatch`'s pattern hardcodes the
  // `/home` segment, surviving a trailing slash or basename change but not a
  // rename of that segment.
  //
  // Keyed by assessment rather than a bare "on the index or not" boolean. The
  // route tree reuses this instance when only the param changes, so a boolean
  // reads a move between two assessments' home pages as no entry at all and
  // hands the second one a warm cache outside the freshness gate. Nothing
  // navigates across assessments today, since every helper pins the param from
  // the current route and the LMS return link is a full page load, which is
  // precisely why the boolean looked right.
  const onHomeIndexRoute = useMatch("/:tenant/:assessmentId/home") !== null;
  const homeIndexKey = onHomeIndexRoute ? assessmentId : null;
  const enteredHomeIndexFor = useRef<string | null>(null);
  const enteringHomeIndex =
    studentHomeEnabled &&
    homeIndexKey !== null &&
    enteredHomeIndexFor.current !== homeIndexKey;

  // Marked pending during render, not from the effect below, and seeded the
  // same way on first mount. Effects run after paint, so setting it there
  // would paint one frame with the charter action already enabled against the
  // cache this refresh exists to replace, which is the race the flag closes.
  // Safe to pair with the effect: both read the same render's values and the
  // ref only moves inside the effect, so they cannot disagree about whether
  // this is an entry and leave the flag stuck on.
  const [workGroupRefreshing, setWorkGroupRefreshing] =
    useState(enteringHomeIndex);
  if (enteringHomeIndex && !workGroupRefreshing) {
    setWorkGroupRefreshing(true);
  }

  useEffect(() => {
    const previous = enteredHomeIndexFor.current;
    enteredHomeIndexFor.current = homeIndexKey;
    if (
      studentHomeEnabled &&
      homeIndexKey !== null &&
      previous !== homeIndexKey
    ) {
      // The feed refetch updates its own hook. The work group is refreshed via
      // a one-off `client.query` (network-only), not `refetchQueries`, which
      // would re-run `HomeRoute`'s active `useWorkGroupQuery` and, on a
      // transient failure, flip it into the error state that renders a
      // full-page `ApiError`. `client.query` writes the cache on success so
      // active watchers pick it up, but keeps a failure to itself. Both
      // rejections are swallowed, and a failed refresh leaves the last-known
      // feed and charter in place: the feed because Apollo keeps the cached
      // payload when a refetch errors (the error surfaces without evicting
      // `data`), the charter because a failed `client.query` never overwrites
      // the cache its active `useWorkGroupQuery` watcher reads.
      void refetch().catch(() => {});
      void client
        .query({
          query: WorkGroupDocument,
          variables: { assessmentId },
          fetchPolicy: "network-only",
        })
        .catch(() => {})
        // Cleared on failure as well. A refresh that never landed leaves the
        // cached charter as the best answer available, the same call the feed
        // above makes, so consumers wait for this to settle rather than for it
        // to succeed.
        //
        // Scoped to the entry that started it. Re-entering the same assessment
        // needs no scoping, because that read carries the same document and
        // variables and Apollo deduplicates it onto the one still in flight.
        // Two different assessments cannot dedupe, so without this an older
        // read landing late would clear the flag the newer entry just set.
        .finally(() => {
          if (enteredHomeIndexFor.current === homeIndexKey) {
            setWorkGroupRefreshing(false);
          }
        });
    }
  }, [homeIndexKey, studentHomeEnabled, refetch, client, assessmentId]);

  return (
    <div ref={manualMountRef} className={styles.root}>
      <RootHeading manualMountElement={manualMountRef} />
      <Navigation
        studentHomeEnabled={studentHomeEnabled}
        badgeCount={badgeCount}
        peerEvaluationEnabled={assessmentSettings?.peerEvaluationEnabled}
        selfEvaluationEnabled={assessmentSettings?.selfEvaluationEnabled}
        sentimentCheckEnabled={assessmentSettings?.sentimentCheckEnabled}
      />
      <div className={styles.bg}>
        <main {...mainContentProps} className={styles.main}>
          <Outlet
            context={
              {
                studentActionFeed,
                networkStatus,
                error,
                loading,
                workGroupRefreshing,
              } satisfies HomeOutletContext
            }
          />
        </main>
      </div>
    </div>
  );
}

interface NavigationProps {
  studentHomeEnabled: boolean;
  badgeCount: number;
  peerEvaluationEnabled?: boolean;
  selfEvaluationEnabled?: boolean;
  sentimentCheckEnabled?: boolean;
}

function Navigation(props: NavigationProps) {
  const { studentHomeEnabled, badgeCount } = props;

  return (
    <nav className={styles.navigation} role="tablist" aria-label="Main">
      <NavLink to="" end className={styles.tab} role="tab">
        {/* Always render a label so role="tab" has an accessible name. Reads
            "Home" when the flag is on, "Preparation" when off. HomeRoute blocks
            until the flag resolves, so this label paints its final value with no
            Preparation-to-Home flip. */}
        <Text kind="bodyLine" className={styles.tabHeader} as="div">
          {studentHomeEnabled ? "Home" : "Preparation"}
        </Text>
        {studentHomeEnabled && badgeCount > 0 && (
          <CountBadge
            count={badgeCount}
            tone="primary"
            aria-label={actionsBadgeLabel(badgeCount)}
            data-testid="home-tab-badge-count"
          />
        )}
      </NavLink>
      <NavLink to="taskboard" className={styles.tab} role="tab">
        <Text kind="bodyLine" className={styles.tabHeader} as="div">
          Task Board
        </Text>
      </NavLink>
      <NavLink to="writing" className={styles.tab} role="tab">
        <Text kind="bodyLine" className={styles.tabHeader} as="div">
          Writing
        </Text>
      </NavLink>
      {props.sentimentCheckEnabled && (
        <NavLink to="sentiment-check" className={styles.tab} role="tab">
          <Text kind="bodyLine" className={styles.tabHeader} as="div">
            Sentiment Check
          </Text>
        </NavLink>
      )}
      {(props.peerEvaluationEnabled === true ||
        props.selfEvaluationEnabled === true) && (
        <NavLink to="peer" className={styles.tab} role="tab">
          <Text kind="bodyLine" className={styles.tabHeader} as="div">
            {props.peerEvaluationEnabled === true
              ? "Peer Evaluation"
              : "Self Evaluation"}
          </Text>
        </NavLink>
      )}
      <NavLink to="insights" className={styles.tab} role="tab">
        <Text kind="bodyLine" className={styles.tabHeader} as="div">
          Group Insights
        </Text>
      </NavLink>
      <NavLink to="feedback" className={styles.tab} role="tab">
        <Text kind="bodyLine" className={styles.tabHeader} as="div">
          Feedback
        </Text>
      </NavLink>
    </nav>
  );
}
